'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check, X, FileInput, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textCompareContent } from '../../data/text-compare-content';
import Link from 'next/link';

export default function TextComparer() {
    const [text1, setText1] = useState('');
    const [text2, setText2] = useState('');
    const [showWhitespace, setShowWhitespace] = useState(false);
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [activeTab, setActiveTab] = useState('side-by-side');
    const { toast } = useToast();

    // Function to compare two texts and find differences
    const compareTexts = () => {
        if (!text1 || !text2) return { same: false, differences: [] };

        const lines1 = text1.split('\n');
        const lines2 = text2.split('\n');
        const maxLength = Math.max(lines1.length, lines2.length);
        const differences = [];

        for (let i = 0; i < maxLength; i++) {
            const line1 = i < lines1.length ? lines1[i] : '';
            const line2 = i < lines2.length ? lines2[i] : '';

            if (caseSensitive ? line1 !== line2 : line1.toLowerCase() !== line2.toLowerCase()) {
                differences.push({
                    line: i + 1,
                    original: line1,
                    changed: line2
                });
            }
        }

        return {
            same: differences.length === 0,
            differences
        };
    };

    const { same, differences } = compareTexts();

    const handleFileUpload = (setText: (text: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setText(fileContent);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
    };

    const clearText = (setText: (text: string) => void) => {
        setText('');
    };

    const swapTexts = () => {
        const temp = text1;
        setText1(text2);
        setText2(temp);
    };

    // Function to highlight differences between two strings
    const highlightDifferences = (str1: string, str2: string) => {
        if (!str1 || !str2) return { __html: str1 || str2 };

        let result = '';
        const maxLength = Math.max(str1.length, str2.length);

        for (let i = 0; i < maxLength; i++) {
            const char1 = i < str1.length ? str1[i] : '';
            const char2 = i < str2.length ? str2[i] : '';

            if ((caseSensitive ? char1 !== char2 : char1.toLowerCase() !== char2.toLowerCase())) {
                result += `<span class="bg-red-100 dark:bg-red-900/50">${char1 || ' '}</span>`;
            } else {
                result += char1 || ' ';
            }
        }

        return { __html: result };
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="mb-8">
                <ToolPageIntro title="Text Compare">
                Compare two text files or strings with our free online tool. Find differences, similarities, and get detailed comparison analysis.
            </ToolPageIntro>
            </div>

            <div className="space-y-6">
                <div className="relative">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <TextAreaSection
                            title="Original Text"
                            value={text1}
                            onChange={(e) => setText1(e.target.value)}
                            onFileUpload={handleFileUpload(setText1)}
                            onClear={() => clearText(setText1)}
                            toast={toast}
                            placeholder="Enter or paste the first text here..."
                        />
                        <TextAreaSection
                            title="Changed Text"
                            value={text2}
                            onChange={(e) => setText2(e.target.value)}
                            onFileUpload={handleFileUpload(setText2)}
                            onClear={() => clearText(setText2)}
                            toast={toast}
                            placeholder="Enter or paste the second text here..."
                        />
                    </div>
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block">
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={swapTexts}
                            className="h-12 w-12 shadow-md hover:bg-accent/50 transition-colors"
                            title="Swap texts"
                        >
                            <RefreshCw className="h-5 w-5" />
                        </Button>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <CardTitle>Comparison Results</CardTitle>
                                <CardDescription>
                                    {same
                                        ? 'The texts are identical!'
                                        : `Found ${differences.length} difference${differences.length !== 1 ? 's' : ''}`}
                                </CardDescription>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="whitespace"
                                        checked={showWhitespace}
                                        onCheckedChange={setShowWhitespace}
                                    />
                                    <Label htmlFor="whitespace">Show whitespace</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Switch
                                        id="case-sensitive"
                                        checked={caseSensitive}
                                        onCheckedChange={setCaseSensitive}
                                    />
                                    <Label htmlFor="case-sensitive">Case sensitive</Label>
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
                                <TabsTrigger value="inline">Inline Differences</TabsTrigger>
                            </TabsList>

                            <TabsContent value="side-by-side" className="mt-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="border rounded-md p-4">
                                        <h3 className="font-medium mb-2">Original</h3>
                                        <div className={`font-mono whitespace-pre-wrap ${showWhitespace ? 'whitespace-pre' : ''}`}>
                                            {text1 || <span className="text-muted-foreground">No text to display</span>}
                                        </div>
                                    </div>
                                    <div className="border rounded-md p-4">
                                        <h3 className="font-medium mb-2">Changed</h3>
                                        <div className={`font-mono whitespace-pre-wrap ${showWhitespace ? 'whitespace-pre' : ''}`}>
                                            {text2 || <span className="text-muted-foreground">No text to display</span>}
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="inline" className="mt-4">
                                <div className="border rounded-md p-4">
                                    {differences.length > 0 ? (
                                        <div className="space-y-4">
                                            {differences.map((diff, index) => (
                                                <div key={index} className="border-b pb-4 last:border-b-0 last:pb-0">
                                                    <div className="text-sm font-medium mb-1">Line {diff.line}</div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <div className="text-sm text-muted-foreground mb-1">Original:</div>
                                                            <div
                                                                className="font-mono p-2 bg-muted/50 rounded"
                                                                dangerouslySetInnerHTML={highlightDifferences(diff.original, diff.changed)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <div className="text-sm text-muted-foreground mb-1">Changed:</div>
                                                            <div
                                                                className="font-mono p-2 bg-muted/50 rounded"
                                                                dangerouslySetInnerHTML={highlightDifferences(diff.changed, diff.original)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            {text1 && text2 ? 'No differences found' : 'Enter text in both fields to compare'}
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={textCompareContent.title}
                description={textCompareContent.description}
                content={textCompareContent.content}
                faqs={textCompareContent.faqs}
                relatedTools={textCompareContent.relatedTools}
            />
        </div>
    );
}

// Reusable text area component
function TextAreaSection({ title, value, onChange, onFileUpload, onClear, toast, placeholder }: {
    title: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onClear: () => void;
    toast: any;
    placeholder: string;
}) {
    const { toast: localToast } = useToast();

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">{title}</CardTitle>
                    <div className="flex items-center space-x-2">
                        {value && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                    const textToCopy = value;
                                    navigator.clipboard.writeText(textToCopy);
                                    (toast || localToast)({
                                        title: 'Copied!',
                                        description: 'Text has been copied to clipboard.',
                                    });
                                }}
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                title="Copy to clipboard"
                            >
                                <Copy className="h-4 w-4" />
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClear}
                            disabled={!value}
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            title="Clear text"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
                <div className="flex-1 flex flex-col">
                    <Textarea
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        className="min-h-[300px] flex-1 font-mono text-sm"
                    />
                    <div className="mt-3 pt-3 border-t flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                            <div>
                                <input
                                    type="file"
                                    id={`file-upload-${title.toLowerCase().replace(' ', '-')}`}
                                    onChange={onFileUpload}
                                    className="hidden"
                                    accept=".txt,.md,.js,.jsx,.ts,.tsx,.json,.html,.css,.scss"
                                />
                                <Label
                                    htmlFor={`file-upload-${title.toLowerCase().replace(' ', '-')}`}
                                    className="text-sm text-muted-foreground hover:text-foreground cursor-pointer inline-flex items-center px-3 py-1.5 rounded-md hover:bg-accent transition-colors"
                                >
                                    <FileInput className="h-4 w-4 mr-2" />
                                    Upload File
                                </Label>
                            </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                            <span className="font-medium">{value.split(/\s+/).filter(Boolean).length}</span> words •{' '}
                            <span className="font-medium">{value.length}</span> chars
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}